import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Kontakt() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Update page title and meta description
    document.title = 'Kontakt Regnskapsfører Oslo | Easy Accounting AS';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Kontakt Easy Accounting AS – regnskapskontor i Oslo. Ring +47 954 87 639, e-post post@easya.no. Brynsveien 2, 0667 Oslo. Gratis 15 min konsultasjon tilgjengelig!');
    }
    
    // Add canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', 'https://easy.no/kontakt');
    
    // Add Schema.org JSON-LD with LocalBusiness and FAQPage
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "mainEntity": {
          "@type": "LocalBusiness",
          "name": "Easy Accounting AS",
          "url": "https://easy.no",
          "telephone": "+47-954-87-639",
          "email": "post@easya.no",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Brynsveien 2",
            "addressLocality": "Oslo",
            "postalCode": "0667",
            "addressCountry": "NO"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "59.9000",
            "longitude": "10.8050"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "08:00",
              "closes": "16:00"
            }
          ],
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+47-954-87-639",
            "contactType": "customer service",
            "email": "post@easya.no",
            "availableLanguage": ["Norwegian", "English"],
            "hoursAvailable": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "08:00",
              "closes": "16:00"
            }
          }
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Hvor mye koster regnskapsføring?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Prisen avhenger av bedriftens størrelse og kompleksitet. Vi tilbyr konkurransedyktige priser fra 2.500 kr/måned for enkle selskaper."
            }
          },
          {
            "@type": "Question",
            "name": "Hvor lang tid tar det å sette opp regnskapet?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Vanligvis 1-2 uker fra vi mottar nødvendig dokumentasjon. Vi sørger for en smidig overgang fra ditt nåværende system."
            }
          },
          {
            "@type": "Question",
            "name": "Tilbyr dere rådgivningstjenester?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ja, vi tilbyr økonomisk rådgivning, budsjettplanlegging og strategisk økonomisk veiledning som en del av våre tjenester."
            }
          },
          {
            "@type": "Question",
            "name": "Kan dere hjelpe med digitalisering?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutt! Vi hjelper bedrifter med å implementere moderne regnskapssystemer og digitale arbeidsflyter."
            }
          }
        ]
      }
    ]);
    document.head.appendChild(schemaScript);
    
    // Load Calendly script automatically
    if (!window.Calendly) {
      const link = document.createElement('link');
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
      
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      script.onload = () => {
        if (window.Calendly) {
          window.Calendly.initBadgeWidget({ 
            url: 'https://calendly.com/easya/30min', 
            text: 'Få kontroll på økonomien – helt gratis! Vi tilbyr 15 minutter gratis konsultasjon med vår regnskapsfører, hvor du får konkrete tips til hvordan bedriften din kan spare tid og penger.', 
            color: '#0069ff', 
            textColor: '#ffffff', 
            branding: false 
          });
        }
      };
      document.head.appendChild(script);
    }
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  const [formData, setFormData] = useState({
    navn: '',
    email: '',
    telefon: '',
    bedrift: '',
    tjeneste: '',
    melding: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3fpvp1nfp0vd7e4nri0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          navn: '',
          email: '',
          telefon: '',
          bedrift: '',
          tjeneste: '',
          melding: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-phone-line',
      title: 'Ring oss',
      info: '+47 954 87 639',
      description: 'Mandag - Fredag: 08:00 - 16:00'
    },
    {
      icon: 'ri-mail-line',  
      title: 'Send e-post',
      info: 'post@easya.no',
      description: 'Vi svarer innen 24 timer'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Besøk oss',
      info: 'Brynsveien 2, 0667 Oslo',
      description: 'Etter avtale'
    }
  ];

  const faqs = [
    {
      question: 'Hvor mye koster regnskapsføring?',
      answer: 'Prisen avhenger av bedriftens størrelse og kompleksitet. Vi tilbyr konkurransedyktige priser fra 2.500 kr/måned for enkle selskaper.'
    },
    {
      question: 'Hvor lang tid tar det å sette opp regnskapet?',
      answer: 'Vanligvis 1-2 uker fra vi mottar nødvendig dokumentasjon. Vi sørger for en smidig overgang fra ditt nåværende system.'
    },
    {
      question: 'Tilbyr dere rådgivningstjenester?',
      answer: 'Ja, vi tilbyr økonomisk rådgivning, budsjettplanlegging og strategisk økonomisk veiledning som en del av våre tjenester.'
    },
    {
      question: 'Kan dere hjelpe med digitalisering?',
      answer: 'Absolutt! Vi hjelper bedrifter med å implementere moderne regnskapssystemer og digitale arbeidsflyter.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl mb-6">
            Kontakt oss
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Vi er her for å hjelpe deg. Ta kontakt for en uforpliktende samtale om dine regnskapsbehov.
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactInfo.map((contact, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${contact.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {contact.title}
                </h3>
                <p className="text-blue-600 font-medium mb-1">
                  {contact.info}
                </p>
                <p className="text-gray-600 text-sm">
                  {contact.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-16 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Send oss en melding
              </h2>
              
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  Takk for din henvendelse! Vi vil kontakte deg snarest.
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  Noe gikk galt. Vennligst prøv igjen eller ring oss direkte.
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form id="kontakt_easy_accounting">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="navn" className="block text-sm font-medium text-gray-700 mb-2">
                      Fullt navn *
                    </label>
                    <input
                      type="text"
                      id="navn"
                      name="navn"
                      value={formData.navn}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Ditt fulle navn"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-post *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus-border-blue-500 text-sm"
                      placeholder="din@epost.no"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      id="telefon"
                      name="telefon"
                      value={formData.telefon}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus-border-blue-500 text-sm"
                      placeholder="+47 12 34 56 78"
                    />
                  </div>
                  <div>
                    <label htmlFor="bedrift" className="block text-sm font-medium text-gray-700 mb-2">
                      Bedriftsnavn
                    </label>
                    <input
                      type="text"
                      id="bedrift"
                      name="bedrift"
                      value={formData.bedrift}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus-border-blue-500 text-sm"
                      placeholder="Din bedrift AS"
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <label htmlFor="tjeneste" className="block text-sm font-medium text-gray-700 mb-2">
                    Hvilken tjeneste er du interessert i?
                  </label>
                  <select
                    id="tjeneste"
                    name="tjeneste"
                    value={formData.tjeneste}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus-border-blue-500 text-sm"
                  >
                    <option value="">Velg en tjeneste</option>
                    <option value="bokforing">Bokføring</option>
                    <option value="regnskap">Regnskap</option>
                    <option value="lonn">Lønn</option>
                    <option value="mva">MVA-rapportering</option>
                    <option value="arsregnskap">Årsregnskap</option>
                    <option value="revisjon">Revisjon</option>
                    <option value="radgivning">Økonomisk rådgivning</option>
                    <option value="annet">Annet</option>
                  </select>
                </div>
                
                <div className="mb-6">
                  <label htmlFor="melding" className="block text-sm font-medium text-gray-700 mb-2">
                    Melding *
                  </label>
                  <textarea
                    id="melding"
                    name="melding"
                    value={formData.melding}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus-border-blue-500 text-sm resize-none"
                    placeholder="Fortell oss om dine behov og hvordan vi kan hjelpe deg..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    Maksimalt 500 tegn ({formData.melding.length}/500)
                  </p>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Sender...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Send melding
                    </>
                  )}
                </Button>
              </form>
            </div>
            
            {/* Map */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2000!2d10.8050!3d59.9000!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46416dc5b3b3b3b3%3A0xabcdef1234567890!2sBrynsveien%202%2C%200667%20Oslo!5e0!3m2!1sno!2sno!4v1700000000001!5m2!1sno!2sno"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Easy Accounting AS - Brynsveien 2, 0667 Oslo"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-4">
              Ofte stilte spørsmål
            </h2>
            <p className="text-lg text-gray-600">
              Her finner du svar på de vanligste spørsmålene vi får
            </p>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
